/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import withErrorStatus from '../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../../shared/Utils/Utils';
import {
  AreaWiseAggregations,
  IDetailComponentProps,
  IEmaasEnergyUsageComponentData,
  IEmaasEnergyUsageComponentDataArea,
  IEmaasEnergyUsageComponentDataZone,
  ISiteCard,
  ZoneWiseAggregations
} from '../../../../DashboardKPIWidgets/IEnergyManagementProps';
import EnergyScopeSavingsByMeterDetailComponent, {
  IEnergyScopeSavingsByMeterDetailComponentProps
} from './EnergyScopeSavingsByMeterDetailComponent';
import EnergyScopeSavingsByAreasDetailComponent, {
  IEnergyBreakDownByArea
} from './EnergyScopeSavingsByAreasDetailComponent';
import { Card } from '@scuf/common';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../../shared/Enums';
import { EnergyUsageUtilityType } from '../../../../shared/Utils/Constants';
import { ApiContext } from '../../../../../context/ApiContext';

type IEnergyData =
  | IEmaasEnergyUsageComponentDataArea
  | IEmaasEnergyUsageComponentData
  | IEmaasEnergyUsageComponentDataZone;
interface IEnergySavingByMeter extends ISiteCard {
  data: IEnergyData;
  UtilityBillType: string;
  subscriptionType: string;
}

const energyScopeSavingsByMeterComponentContent = (
  props: IEnergySavingByMeter
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, UtilityBillType, subscriptionType } = props;
  const context = useContext(ApiContext);
  const { dataSourceType } = context;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  const isfloorWiseBreakdown =
    (subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION ||
      data.subscriptionType === EnergyUsageField.PREMIUMSUBS) &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER;
  const childProps = {
    data,
    UtilityBillType
  };
  return (
    <div>
      {isfloorWiseBreakdown ? (
        <EnergyScopeSavingsByAreasDetailComponent
          {...(childProps as IEnergyBreakDownByArea)}
        />
      ) : (
        <EnergyScopeSavingsByMeterDetailComponent
          {...(childProps as IEnergyScopeSavingsByMeterDetailComponentProps)}
        />
      )}
    </div>
  );
};

const EnergyScopeSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(energyScopeSavingsByMeterComponentContent)
);

const energyScopeSavingsByMeterComponent = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    dataConnector,
    UtilityBillType,
    subscriptionType,
    setHeader,
    utilityType
  } = props;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const carbonScopeOneSavingsData = (all as unknown) as IEnergyData;
  const isDataAvailable = carbonScopeOneSavingsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [subscriptionCheck, setSubscriptionCheck] = useState('Basic');
  const [data, setData] = useState({} as IEnergyData);
  const context = useContext(ApiContext);
  const { dataSourceType } = context;
  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        const zoneWithMeterIds: IEnergyData = JSON.parse(
          JSON.stringify(carbonScopeOneSavingsData)
        );
        if (props.toggleSeparateApi) {
          zoneWithMeterIds.areaWiseAggregations =
            zoneWithMeterIds.areaWiseAggregations &&
            zoneWithMeterIds.areaWiseAggregations.map(
              (item: ZoneWiseAggregations) => {
                item.meterWiseAggregations = item.sortedMeterIds.map(
                  (mtr: { id: string }) => {
                    return {
                      id: mtr.id,
                      sourceType: zoneWithMeterIds.sourceType
                    };
                  }
                );
                return item;
              }
            );
        }

        setData(zoneWithMeterIds);
      }
      setErrorInfo(error);
      setStatusCode(status);
      setSubscriptionCheck(data?.subscriptionType);
    }
  }, [isDataAvailable, loading, status]);

  const header =
    (subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION ||
      subscriptionCheck === EnergyUsageField.PREMIUMSUBS) &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER
      ? t(d.areasHeader)
      : t(d.breakdownCarbon);
  if (setHeader !== undefined) {
    setHeader(header);
  }

  return (
    <Card className="Savemeter">
      {/* <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        {header}
      </Card.Header> */}
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <EnergyScopeSavingsByMeterComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            UtilityBillType={UtilityBillType!}
            subscriptionType={subscriptionType!}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default energyScopeSavingsByMeterComponent;
